import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { EyeSlash, Eye, ArrowsClockwise, CaretDown, CaretRight } from "@phosphor-icons/react";

// Admin panel: per-tenant module visibility. Hidden modules disappear from the
// sidebar UI but background APIs keep running (data flows normally for
// referential integrity, cross-module scoring, etc.).
export default function ModuleSettings() {
  const [catalog, setCatalog] = useState([]);
  const [hidden, setHidden] = useState(new Set());
  const [initialHidden, setInitialHidden] = useState(new Set());
  const [tierLocked, setTierLocked] = useState(new Set());
  const [submodules, setSubmodules] = useState({});
  const [hiddenSub, setHiddenSub] = useState(new Set());
  const [initialHiddenSub, setInitialHiddenSub] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/modules")
      .then((r) => {
        setCatalog(r.data.catalog || []);
        const set = new Set(r.data.hidden_modules || []);
        setHidden(set);
        setInitialHidden(new Set(set));
        setTierLocked(new Set(r.data.tier_locked || []));
        setSubmodules(r.data.submodules || {});
        const sub = new Set(r.data.hidden_submodules || []);
        setHiddenSub(sub);
        setInitialHiddenSub(new Set(sub));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(load, []);

  const toggle = (key) => {
    const nx = new Set(hidden);
    if (nx.has(key)) nx.delete(key); else nx.add(key);
    setHidden(nx);
  };

  const toggleSub = (key) => {
    const nx = new Set(hiddenSub);
    if (nx.has(key)) nx.delete(key); else nx.add(key);
    setHiddenSub(nx);
  };

  const toggleExpand = (key) => {
    const nx = new Set(expanded);
    if (nx.has(key)) nx.delete(key); else nx.add(key);
    setExpanded(nx);
  };

  const setBulk = (keys, shouldHide) => {
    const nx = new Set(hidden);
    keys.forEach((k) => { if (shouldHide) nx.add(k); else nx.delete(k); });
    setHidden(nx);
  };

  // Persist an explicit set immediately (used by the one-click "hide unavailable").
  const persist = async (nextSet, msg, nextSubSet) => {
    setBusy(true);
    try {
      const subArr = Array.from(nextSubSet || hiddenSub);
      await api.put("/admin/modules", { hidden_modules: Array.from(nextSet), hidden_submodules: subArr });
      setHidden(new Set(nextSet));
      setInitialHidden(new Set(nextSet));
      setHiddenSub(new Set(subArr));
      setInitialHiddenSub(new Set(subArr));
      toast.success(msg || "Module visibility updated. Refresh the page to see the new sidebar.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    await persist(hidden, undefined, hiddenSub);
  };

  const subDirty = hiddenSub.size !== initialHiddenSub.size || [...hiddenSub].some((k) => !initialHiddenSub.has(k));
  const dirty = hidden.size !== initialHidden.size || [...hidden].some((k) => !initialHidden.has(k)) || subDirty;
  const hiddenCount = hidden.size;

  return (
    <div className="space-y-6" data-testid="module-settings">
      <div className="border border-border bg-card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <EyeSlash size={22} className="text-primary mt-0.5" />
          <div>
            <h3 className="font-display font-bold text-lg">Module Visibility</h3>
            <p className="text-sm text-muted-foreground">
              Turn off modules you don&apos;t use so they disappear from the sidebar. Background APIs continue
              to work &mdash; other modules that reference their data (evidence, tracker, CFR, etc.) will keep
              functioning normally. Perfect when your team uses another ticketing tool like EZVista instead of
              ClaroFlow, or keeps its own PSMP records outside the platform.
            </p>
          </div>
        </div>
      </div>

      {(() => {
        const activeModules = catalog.filter((m) => !m.coming_soon);
        const soonModules = catalog.filter((m) => m.coming_soon);
        const soonKeys = soonModules.map((m) => m.key);
        const allSoonHidden = soonKeys.length > 0 && soonKeys.every((k) => hidden.has(k));

        // "Unavailable" = Coming Soon placeholders + modules this entity's plan can't use.
        const unavailableKeys = Array.from(new Set([
          ...soonKeys,
          ...catalog.filter((m) => tierLocked.has(m.key)).map((m) => m.key),
        ]));
        const allUnavailableHidden = unavailableKeys.length > 0 && unavailableKeys.every((k) => hidden.has(k));
        const hideAllUnavailable = () => {
          const nx = new Set(hidden);
          if (allUnavailableHidden) unavailableKeys.forEach((k) => nx.delete(k));
          else unavailableKeys.forEach((k) => nx.add(k));
          persist(nx, allUnavailableHidden
            ? "Unavailable modules restored to the sidebar."
            : `Hid ${unavailableKeys.length} unavailable module${unavailableKeys.length === 1 ? "" : "s"}. Refresh to see the new sidebar.`);
        };

        const renderRow = (m) => {
          const isHidden = hidden.has(m.key);
          const offPlan = tierLocked.has(m.key);
          const subs = submodules[m.key] || [];
          const isOpen = expanded.has(m.key);
          const subHiddenCount = subs.filter((s) => hiddenSub.has(s.key)).length;
          return (
            <li key={m.key} data-testid={`module-row-${m.key}`}>
              <div className="flex items-center gap-3 px-4 py-2.5">
                <Checkbox
                  data-testid={`module-toggle-${m.key}`}
                  checked={!isHidden}
                  onCheckedChange={() => toggle(m.key)}
                  className="rounded-none"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{m.label}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{m.key}</div>
                </div>
                {subs.length > 0 && !isHidden && (
                  <button
                    data-testid={`module-expand-${m.key}`}
                    onClick={() => toggleExpand(m.key)}
                    className="flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                  >
                    {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                    {subs.length} parts{subHiddenCount > 0 ? ` (${subHiddenCount} off)` : ""}
                  </button>
                )}
                {offPlan && (
                  <span className="text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-700">
                    Off-plan
                  </span>
                )}
                <span className={`text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 border ${isHidden ? "border-slate-300 bg-slate-50 text-slate-500" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
                  {isHidden ? "Hidden" : "Visible"}
                </span>
              </div>
              {subs.length > 0 && isOpen && !isHidden && (
                <ul className="bg-muted/30 border-t border-border" data-testid={`submodule-list-${m.key}`}>
                  {subs.map((s) => {
                    const subOff = hiddenSub.has(s.key);
                    return (
                      <li key={s.key} className="flex items-center gap-3 pl-12 pr-4 py-2" data-testid={`submodule-row-${s.key}`}>
                        <Checkbox
                          data-testid={`submodule-toggle-${s.key}`}
                          checked={!subOff}
                          onCheckedChange={() => toggleSub(s.key)}
                          className="rounded-none"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm">{s.label}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{s.key}</div>
                        </div>
                        <span className={`text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 border ${subOff ? "border-slate-300 bg-slate-50 text-slate-500" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
                          {subOff ? "Hidden" : "Visible"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        };

        return (
          <>
            {unavailableKeys.length > 0 && (
              <div className="border border-primary/30 bg-primary/5 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="hide-unavailable-banner">
                <div className="flex items-start gap-3">
                  <EyeSlash size={20} className="text-primary mt-0.5" />
                  <div>
                    <div className="font-display font-bold text-sm">Hide all unavailable modules</div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      One click hides everything this entity can&apos;t use yet &mdash;{" "}
                      {soonKeys.length > 0 && `${soonKeys.length} “Coming Soon” placeholder${soonKeys.length === 1 ? "" : "s"}`}
                      {soonKeys.length > 0 && tierLocked.size > 0 && " + "}
                      {tierLocked.size > 0 && `${catalog.filter((m) => tierLocked.has(m.key)).length} not in your plan`}
                      {" "}&mdash; and saves immediately.
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="hide-unavailable-toggle"
                  onClick={hideAllUnavailable}
                  disabled={busy}
                  className="rounded-none bg-primary hover:bg-primary/90 shrink-0"
                >
                  {busy
                    ? <><ArrowsClockwise size={14} className="mr-1 animate-spin" />Saving…</>
                    : allUnavailableHidden
                      ? <><Eye size={14} className="mr-1" />Show unavailable ({unavailableKeys.length})</>
                      : <><EyeSlash size={14} className="mr-1" />Hide all unavailable ({unavailableKeys.length})</>}
                </Button>
              </div>
            )}

            <div className="border border-border bg-card">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <Label className="font-display font-bold">Active Modules</Label>
                  <span className="text-xs text-muted-foreground" data-testid="module-hidden-count">
                    {hiddenCount === 0 ? "none hidden" : `${hiddenCount} hidden`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="module-select-none"
                    variant="outline"
                    size="sm"
                    onClick={() => setHidden(new Set())}
                    className="rounded-none"
                  >
                    <Eye size={13} className="mr-1" />Show all
                  </Button>
                  <Button
                    data-testid="module-save"
                    onClick={save}
                    disabled={!dirty || busy}
                    className="rounded-none bg-primary hover:bg-primary/90"
                  >
                    {busy ? <><ArrowsClockwise size={14} className="mr-1 animate-spin" />Saving…</> : "Save"}
                  </Button>
                </div>
              </div>
              <ul className="divide-y divide-border">
                {activeModules.map(renderRow)}
              </ul>
            </div>

            {soonModules.length > 0 && (
              <div className="border border-border bg-card" data-testid="module-soon-section">
                <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-amber-50/50">
                  <div>
                    <Label className="font-display font-bold">Coming Soon Placeholders</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Sidebar entries with a &ldquo;Soon&rdquo; badge. Hide them until the real module ships &mdash;
                      when a module goes live it will replace the placeholder automatically.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="module-soon-hide-all"
                      variant="outline"
                      size="sm"
                      onClick={() => setBulk(soonKeys, !allSoonHidden)}
                      className="rounded-none"
                    >
                      {allSoonHidden
                        ? <><Eye size={13} className="mr-1" />Show all Soon</>
                        : <><EyeSlash size={13} className="mr-1" />Hide all Soon</>}
                    </Button>
                  </div>
                </div>
                <ul className="divide-y divide-border">
                  {soonModules.map(renderRow)}
                </ul>
              </div>
            )}
          </>
        );
      })()}

      {dirty && (
        <div className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="module-dirty-hint">
          You have unsaved changes. Click <b>Save</b> to apply. After saving, refresh your browser to see the sidebar update.
        </div>
      )}
    </div>
  );
}
